import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const formSelector =
  '.kinriot-int__form, .kinriot-rtw__form, .kinriot-cart-upsell__form, form[id^="CharmWardrobe-"]:not([data-kcw-preview-form]), [data-preview-mode="false"] .rn-form';

const roadnestControlStates = new WeakMap();

function getLiveRegion(form) {
  let region = form.querySelector('[data-kinriot-cart-status]');

  if (!region) {
    region = document.createElement('span');
    region.className = 'visually-hidden';
    region.dataset.kinriotCartStatus = '';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    form.appendChild(region);
  }

  return region;
}

function setSubmitting(form, submitting) {
  const button = form.querySelector('[data-add-button], [data-kcw-add]');
  form.toggleAttribute('aria-busy', submitting);
  form.dataset.kinriotSubmitting = submitting ? 'true' : 'false';

  if (button instanceof HTMLButtonElement) {
    button.disabled = submitting;
  }
  if (form.classList.contains('rn-form')) {
    if (submitting) {
      const states = [...form.querySelectorAll('fieldset, [data-quantity], [data-quantity-minus], [data-quantity-plus]')].map((control) => [control, control.disabled]);
      roadnestControlStates.set(form, states);
      states.forEach(([control]) => { control.disabled = true; });
    } else {
      (roadnestControlStates.get(form) || []).forEach(([control, disabled]) => { control.disabled = disabled; });
      roadnestControlStates.delete(form);
    }
    form.dispatchEvent(new CustomEvent('kinriot:submitting', { detail: { submitting } }));
  }
}

async function addToCart(event) {
  if (!(event.target instanceof HTMLFormElement)) return;

  const form = event.target.closest(formSelector);
  if (!(form instanceof HTMLFormElement)) return;

  const submitter = event.submitter;
  const isUpsell = form.classList.contains('kinriot-cart-upsell__form');

  if (!isUpsell && submitter instanceof HTMLElement && submitter.getAttribute('name') !== 'add') return;
  if (form.dataset.kinriotSubmitting === 'true') {
    event.preventDefault();
    return;
  }

  const addButton = form.querySelector('[data-add-button], [data-kcw-add]');
  if (addButton instanceof HTMLButtonElement && addButton.disabled) return;
  if (!form.checkValidity()) return;

  const formData = new FormData(form);
  const variantId = formData.get('id');
  if (!variantId) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  setSubmitting(form, true);

  const liveRegion = getLiveRegion(form);
  liveRegion.textContent = '';

  const sections = new Set();
  for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      sections.add(component.dataset.sectionId);
    }
  }

  if (sections.size > 0) {
    formData.set('sections', Array.from(sections).join(','));
    formData.set('sections_url', window.location.pathname);
  }

  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();

  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: isUpsell ? 'cart' : 'product',
      lines: [{ merchandiseId: String(variantId), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
      credentials: 'same-origin',
    });

    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Unable to add this item.');
    }

    const cartResponse = await fetch(Theme.routes.cart_url + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!cartResponse.ok) {
      throw new Error('Unable to refresh the cart.');
    }

    const ajaxCart = await cartResponse.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'kinriot-ajax-cart',
        sourceId: form.id || '',
        itemCount: quantity,
        productId: form.dataset.productId,
        sections: result.sections,
        didError: false,
      },
    });

    liveRegion.textContent = Theme.translations.added;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unable to add this item.';
    liveRegion.textContent = message;
    deferred.reject(error);

    form.dispatchEvent(
      new CartErrorEvent({
        error: message,
        code: 'INVALID',
      })
    );
  } finally {
    setSubmitting(form, false);
  }
}

if (!window.__kinriotAjaxCartBound) {
  window.__kinriotAjaxCartBound = true;
  document.addEventListener('submit', addToCart, true);
}
